import React, { useState, useEffect } from 'react';
import { extinguisherService } from '../services/api';
import UserManagement from './UserManagement';

function AdminDashboard() {
  const [extinguishers, setExtinguishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('extinguishers');

  useEffect(() => {
    loadExtinguishers();
  }, []);

  const loadExtinguishers = async () => {
    try {
      const response = await extinguisherService.getAll();
      setExtinguishers(response.data);
    } catch (error) {
      setError('Failed to load extinguishers');
      console.error('Load error:', error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this extinguisher?')) {
      return;
    }

    try {
      await extinguisherService.delete(id);
      setExtinguishers(extinguishers.filter(ext => ext.id !== id));
    } catch (error) {
      setError('Failed to delete extinguisher');
      console.error('Delete error:', error);
    }
  };

  const isExpiringSoon = (expirationDate) => {
    const today = new Date();
    const expiration = new Date(expirationDate);
    const daysUntilExpiration = Math.ceil((expiration - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiration <= 30;
  };

  const isExpired = (expirationDate) => {
    const today = new Date();
    const expiration = new Date(expirationDate);
    return expiration < today;
  };

  const getStatus = (expirationDate) => {
    if (isExpired(expirationDate)) return 'Expired';
    if (isExpiringSoon(expirationDate)) return 'Expiring Soon';
    return 'Valid';
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Address', 'Latitude', 'Longitude', 'Expiration Date', 'Status', 'Created Date', 'User ID'];
    
    const csvData = extinguishers.map(ext => [
      ext.id,
      `"${ext.address}"`, // Quote address in case it contains commas
      ext.lat,
      ext.lng,
      new Date(ext.expiration).toLocaleDateString(),
      getStatus(ext.expiration),
      new Date(ext.created_at).toLocaleDateString(),
      ext.user_id || 'N/A'
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const today = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `fire-extinguishers-${today}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <div>Loading dashboard...</div>;
  }

  return (
    <div className="admin-dashboard">
      <h2>Admin Dashboard</h2>
      {error && <div className="error-message">{error}</div>}
      
      {/* Tab Navigation */}
      <div style={{ marginBottom: '2rem', borderBottom: '1px solid #dee2e6' }}>
        <div style={{ display: 'flex', gap: '0px' }}>
          <button 
            onClick={() => setActiveTab('extinguishers')}
            style={{
              padding: '1rem 2rem',
              border: 'none',
              backgroundColor: activeTab === 'extinguishers' ? '#007bff' : 'transparent',
              color: activeTab === 'extinguishers' ? 'white' : '#007bff',
              borderBottom: activeTab === 'extinguishers' ? '2px solid #007bff' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Fire Extinguishers
          </button>
          <button 
            onClick={() => setActiveTab('users')}
            style={{
              padding: '1rem 2rem',
              border: 'none',
              backgroundColor: activeTab === 'users' ? '#007bff' : 'transparent',
              color: activeTab === 'users' ? 'white' : '#007bff',
              borderBottom: activeTab === 'users' ? '2px solid #007bff' : '2px solid transparent',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            User Management
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'extinguishers' && (
        <div>
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Statistics</h3>
              <button 
                onClick={exportToCSV}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                disabled={extinguishers.length === 0}
              >
                📊 Export to CSV
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div style={{ background: '#e8f5e8', padding: '1rem', borderRadius: '4px' }}>
                <strong>{extinguishers.length}</strong><br />
                Total Extinguishers
              </div>
              <div style={{ background: '#fff3cd', padding: '1rem', borderRadius: '4px' }}>
                <strong>{extinguishers.filter(ext => isExpiringSoon(ext.expiration) && !isExpired(ext.expiration)).length}</strong><br />
                Expiring Soon (30 days)
              </div>
              <div style={{ background: '#f8d7da', padding: '1rem', borderRadius: '4px' }}>
                <strong>{extinguishers.filter(ext => isExpired(ext.expiration)).length}</strong><br />
                Expired
              </div>
            </div>
          </div>

          <h3>All Fire Extinguishers</h3>
          
          {extinguishers.length === 0 ? (
            <p>No fire extinguishers reported yet.</p>
          ) : (
            <div className="extinguisher-list">
              {extinguishers.map((extinguisher) => (
                <div 
                  key={extinguisher.id} 
                  className="extinguisher-card"
                  style={{
                    borderLeft: isExpired(extinguisher.expiration) 
                      ? '4px solid #dc3545' 
                      : isExpiringSoon(extinguisher.expiration) 
                        ? '4px solid #ffc107' 
                        : '4px solid #28a745'
                  }}
                >
                  <div className="extinguisher-details">
                    <div className="extinguisher-info">
                      <h3>{extinguisher.address}</h3>
                      <p><strong>Expiration:</strong> {new Date(extinguisher.expiration).toLocaleDateString()}</p>
                      <p><strong>Coordinates:</strong> {extinguisher.lat.toFixed(4)}, {extinguisher.lng.toFixed(4)}</p>
                      <p><strong>Reported:</strong> {new Date(extinguisher.created_at).toLocaleDateString()}</p>
                      {extinguisher.user_id && <p><strong>User ID:</strong> {extinguisher.user_id}</p>}
                      
                      <div style={{ marginTop: '1rem' }}>
                        <button 
                          className="btn" 
                          style={{ backgroundColor: '#dc3545', marginRight: '0.5rem' }}
                          onClick={() => handleDelete(extinguisher.id)}
                        >
                          Delete
                        </button>
                        {isExpired(extinguisher.expiration) && (
                          <span style={{ color: '#dc3545', fontWeight: 'bold' }}>EXPIRED</span>
                        )}
                        {isExpiringSoon(extinguisher.expiration) && !isExpired(extinguisher.expiration) && (
                          <span style={{ color: '#ffc107', fontWeight: 'bold' }}>EXPIRING SOON</span>
                        )}
                      </div>
                    </div>
                    
                    {extinguisher.image_url && (
                      <div>
                        <img 
                          src={extinguisher.image_url} 
                          alt="Fire Extinguisher" 
                          className="extinguisher-image"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <UserManagement />
      )}
    </div>
  );
}

export default AdminDashboard;